// Date helpers for "calendar-day" values (pagos, reembolsos, etc.) where the
// time-of-day is irrelevant — only the picked day matters, interpreted in the
// venue's own timezone.
//
// Background: `new Date("YYYY-MM-DD")` parses as UTC midnight (per JS spec).
// In a negative-offset venue (e.g. America/Mexico_City, UTC-6) that renders
// as the previous day at 18:00, so every pago saved that way lands on the
// wrong calendar date when read back with `toLocaleDateString()`.
//
// Going forward we store the picked day at *noon in the venue's timezone*,
// which is timezone-safe for display. Legacy records (UTC 00:00:00.000) are
// detected via fingerprint and formatted using UTC date components so they
// still surface the day the user originally picked.

import { Timestamp } from "firebase/firestore";
import { DateTime } from "luxon";

const DEFAULT_TZ = "America/Mexico_City";

const toJsDate = (val) => {
  if (val == null) return null;
  if (val instanceof Date) return val;
  if (typeof val.toDate === "function") return val.toDate();
  if (typeof val.seconds === "number") return new Date(val.seconds * 1000);
  if (typeof val === "string" || typeof val === "number") return new Date(val);
  return null;
};

// A legacy record is one saved via `Timestamp.fromDate(new Date("YYYY-MM-DD"))`,
// which always lands on exactly UTC 00:00:00.000.
//
// OJO: la huella es ambigua para un INSTANTE real. Un evento a las 18:00 en
// CDMX (UTC-6) es exactamente 00:00 UTC, y leerlo como legacy lo manda al
// día siguiente (y al mes siguiente si es fin de mes). Por eso el heurístico
// aplica solo a campos de "día calendario" (fecha de pago, de costo, de
// depósito), nunca a startTime/endTime: los callers de instantes pasan
// `INSTANTE` y se interpreta el momento real en la zona del venue.
const isLegacyUtcMidnight = (d) =>
  d.getUTCHours() === 0 &&
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0;

// Returns a Date pinned to UTC noon of the *intended* calendar day for the
// given value. Use this as the canonical anchor for display, filtering, and
// year/month bucketing — it's stable regardless of viewer timezone.
const toCalendarDayUTC = (val, timeZone, { instante = false } = {}) => {
  const d = toJsDate(val);
  if (!d || isNaN(d.getTime())) return null;
  let y, m, day;
  if (!instante && isLegacyUtcMidnight(d)) {
    y = d.getUTCFullYear();
    m = d.getUTCMonth();
    day = d.getUTCDate();
  } else {
    const dt = DateTime.fromJSDate(d).setZone(timeZone || DEFAULT_TZ);
    if (!dt.isValid) return null;
    y = dt.year;
    m = dt.month - 1;
    day = dt.day;
  }
  return new Date(Date.UTC(y, m, day, 12, 0, 0));
};

// "YYYY-MM-DD" de HOY para proponer en un <input type="date">. Usa los
// componentes locales del navegador, NO toISOString(): a las 7 pm en CDMX
// el día UTC ya es mañana, y un egreso propuesto "mañana" cae en el mes
// siguiente cuando es 30 o 31.
export const todayInputValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// Parse a "YYYY-MM-DD" string (from <input type="date">) into a Firestore
// Timestamp at noon in the venue's timezone.
export const parseDateInputToTimestamp = (dateString, timeZone) => {
  if (!dateString || typeof dateString !== "string") return null;
  const dt = DateTime.fromISO(dateString, { zone: timeZone || DEFAULT_TZ }).set({
    hour: 12,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  if (!dt.isValid) return null;
  return Timestamp.fromDate(dt.toJSDate());
};

// Format a Timestamp / Date / ISO string / seconds-object as a calendar date,
// in the venue's timezone. Legacy UTC-midnight records are formatted from
// their UTC components so the displayed day matches what the user originally
// picked. Defaults to a long es-MX form ("5 de abril de 2026") for
// human-readable, unambiguous output.
const LONG_OPTS = { day: "numeric", month: "long", year: "numeric" };
const SHORT_OPTS = { day: "2-digit", month: "2-digit", year: "numeric" };

export const formatCalendarDate = (val, timeZone, opts = {}) => {
  const { locale = "es-MX", style = "long", instante = false, ...rest } = opts;
  const anchor = toCalendarDayUTC(val, timeZone, { instante });
  if (!anchor) return null;
  const base = style === "short" ? SHORT_OPTS : LONG_OPTS;
  return anchor.toLocaleDateString(locale, { ...base, ...rest, timeZone: "UTC" });
};

// "lun 8 dic 2026" — el día de la SEMANA es dato de negocio (un sábado no
// vale lo mismo que un martes) y la forma larga no cabe en una tabla. Se
// arma por partes porque el patrón de es-MX mete preposiciones y puntos
// ("lun, 8 de dic de 2026") que aquí solo estorban.
export const formatDiaEvento = (val, timeZone, opts = {}) => {
  const { locale = "es-MX", instante = false } = opts;
  const anchor = toCalendarDayUTC(val, timeZone, { instante });
  if (!anchor) return null;
  const partes = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).formatToParts(anchor);
  const parte = (tipo) =>
    (partes.find((p) => p.type === tipo)?.value || "").replace(/\.$/, "");
  const dia = parte("day");
  const mes = parte("month");
  if (!dia || !mes) return null;
  return [parte("weekday"), dia, mes, parte("year")].filter(Boolean).join(" ");
};

// Canonical calendar-day anchor (UTC noon). Exposed for callers that need
// to sort, compare, or place a record in a range. Tercer parámetro opcional
// `{ instante: true }` (o la constante INSTANTE) para startTime/endTime.
export const calendarDayAnchor = toCalendarDayUTC;

// Para pasar como último argumento cuando el valor es un instante real
// (startTime/endTime de un evento), no un día capturado en un <input date>.
export const INSTANTE = Object.freeze({ instante: true });

// Year for a calendar-day value, applying the legacy heuristic.
export const calendarYear = (val, timeZone, opts) => {
  const anchor = toCalendarDayUTC(val, timeZone, opts);
  return anchor ? anchor.getUTCFullYear() : null;
};

// El DÍA de un instante (startTime de un evento) convertido a un valor de
// "día calendario": mediodía en la zona del venue, como lo guarda
// parseDateInputToTimestamp. Es lo que hay que escribir en `costo.fecha`
// cuando la fecha del costo es la del evento: copiar startTime tal cual
// dejaba un instante (18:00 CDMX = 00:00 UTC) que después se leía como
// legacy y caía al día siguiente.
export const fechaDeDia = (val, timeZone) => {
  const anchor = toCalendarDayUTC(val, timeZone, { instante: true });
  if (!anchor) return null;
  const iso = `${anchor.getUTCFullYear()}-${String(anchor.getUTCMonth() + 1).padStart(2, "0")}-${String(
    anchor.getUTCDate()
  ).padStart(2, "0")}`;
  return parseDateInputToTimestamp(iso, timeZone);
};

// Month/year ranges expressed in UTC, matching the canonical anchor above.
// `monthStr` is "YYYY-MM".
export const getMonthRange = (monthStr) => {
  const [year, month] = monthStr.split("-").map(Number);
  const from = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
  const to = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
  return { from, to };
};

export const getYearRange = (year) => {
  const from = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
  const to = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
  return { from, to };
};

// True iff the value's canonical calendar day falls within [from, to].
export const isInRange = (val, from, to, timeZone, opts) => {
  const anchor = toCalendarDayUTC(val, timeZone, opts);
  if (!anchor) return false;
  return anchor >= from && anchor <= to;
};
